import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, type StateContext } from '@ngxs/store';
import { CountryHttpService } from '../services/country-http.service';
import { TimeZoneService } from '../services/time-zone.service';
import { type ICountriesDto, type ITimeZonesDto, type ICountryPhoneMetaDto } from '@pecan/api';
import { GetCountries, GetTimeZones } from './location.actions';
import { type ILocationStateModel } from './location.model';
import { type Observable, tap } from 'rxjs';

const defaults: ILocationStateModel = {
    timeZonesInfo: null,
    countries: null,
} as const;

@State<ILocationStateModel>({
    name: 'locationState',
    defaults,
})
@Injectable()
export class LocationState {
    private readonly _timeZoneService: TimeZoneService = inject(TimeZoneService);

    private readonly _countryHttpService: CountryHttpService = inject(CountryHttpService);

    @Selector()
    public static countries(state: ILocationStateModel): ICountryPhoneMetaDto[] | null {
        const countries = state?.countries?.countries;

        if (!countries) {
            return null;
        }

        return Object.keys(countries).map((countryName: string) => ({
            countryCode: countries[countryName].phoneCode,
            isoCode: countries[countryName].isoCode,
            name: countryName,
        }));
    }

    @Selector()
    public static areaCodes(state: ILocationStateModel): Record<string, string> | null {
        if (!state.timeZonesInfo?.areaCodes) {
            return null;
        }

        return state.timeZonesInfo.areaCodes;
    }

    @Selector()
    public static timeZones(state: ILocationStateModel): Record<string, string> | null {
        if (!state.timeZonesInfo?.timeZones) {
            return null;
        }

        return state.timeZonesInfo.timeZones;
    }

    @Selector()
    public static timeZoneKeyValueList(state: ILocationStateModel): Record<string, string>[] | null {
        if (!state.timeZonesInfo?.timeZones) {
            return null;
        }

        return Object.entries(state.timeZonesInfo.timeZones).map(([key, value]: [string, string]) => ({ key, value }));
    }

    @Action(GetTimeZones)
    public getTimeZones(ctx: StateContext<ILocationStateModel>): Observable<ITimeZonesDto> {
        return this._timeZoneService.getTimeZones().pipe(
            tap((timeZonesInfo: ITimeZonesDto) => {
                ctx.patchState({ timeZonesInfo });
            })
        );
    }

    @Action(GetCountries)
    public getCountries(ctx: StateContext<ILocationStateModel>): Observable<ICountriesDto> {
        return this._countryHttpService.getCountries().pipe(
            tap((countries: ICountriesDto) => {
                ctx.patchState({ countries });
            })
        );
    }
}
